import { useEffect, useState, useRef, useCallback } from 'react';
import { soundFx } from '../utils/soundSynthesizer';

interface ReelSpinnerProps {
    targetTicket: string | number;
    isSpinning: boolean;
    onComplete?: () => void;
}

export default function ReelSpinner({
    targetTicket,
    isSpinning,
    onComplete,
}: ReelSpinnerProps) {
    // Sanitize ticket string to pure digits
    const rawDigits = String(targetTicket || '').replace(/\D/g, '');
    const digitCount = Math.max(6, rawDigits.length || 6);
    const targetPadded = (rawDigits || '000000').padStart(digitCount, '0');

    // Never initialize with targetPadded when isSpinning is true!
    const [displayedDigits, setDisplayedDigits] = useState<string[]>(() => {
        if (!isSpinning) return targetPadded.split('');
        return Array.from({ length: digitCount }, () => String(Math.floor(Math.random() * 10)));
    });
    const [lockedArray, setLockedArray] = useState<boolean[]>(() =>
        Array(digitCount).fill(!isSpinning)
    );

    const isRunningRef = useRef<boolean>(false);
    const timersRef = useRef<NodeJS.Timeout[]>([]);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const clearTimers = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        timersRef.current.forEach(t => clearTimeout(t));
        timersRef.current = [];
        isRunningRef.current = false;
    }, []);

    useEffect(() => {
        clearTimers();

        if (!isSpinning) {
            setDisplayedDigits(targetPadded.split(''));
            setLockedArray(Array(digitCount).fill(true));
            return;
        }

        // Start drawing animation
        isRunningRef.current = true;
        const currentLocked = Array(digitCount).fill(false);
        setLockedArray(currentLocked);
        setDisplayedDigits(Array.from({ length: digitCount }, () => String(Math.floor(Math.random() * 10))));

        let tickCount = 0;
        intervalRef.current = setInterval(() => {
            if (!isRunningRef.current) return;
            setDisplayedDigits(prev =>
                prev.map((val, idx) => {
                    if (currentLocked[idx]) return targetPadded[idx];
                    return String(Math.floor(Math.random() * 10));
                })
            );

            tickCount++;
            if (tickCount % 3 === 0) {
                soundFx.playTick(480 + Math.random() * 200);
            }
        }, 45);

        // Progressively lock each lottery ball
        const baseDelay = 600;
        const stepDelay = 320;

        for (let i = 0; i < digitCount; i++) {
            const timeout = setTimeout(() => {
                currentLocked[i] = true;
                setLockedArray([...currentLocked]);

                setDisplayedDigits(prev => {
                    const next = [...prev];
                    next[i] = targetPadded[i];
                    return next;
                });

                soundFx.playLock(i);

                if (i === digitCount - 1) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    isRunningRef.current = false;
                    const finalTimeout = setTimeout(() => {
                        if (onComplete) onComplete();
                    }, 300);
                    timersRef.current.push(finalTimeout);
                }
            }, baseDelay + i * stepDelay);

            timersRef.current.push(timeout);
        }

        return () => {
            clearTimers();
        };
    }, [isSpinning, targetPadded, digitCount, clearTimers, onComplete]);

    const allLocked = lockedArray.every(Boolean);

    return (
        <div className={`lottery-drum-container ${allLocked ? 'all-revealed' : ''}`}>
            {displayedDigits.map((digit, idx) => {
                const isLocked = lockedArray[idx];
                return (
                    <div
                        key={idx}
                        className={`lottery-sphere ${isLocked ? 'locked' : 'spinning'}`}
                    >
                        <span>{digit}</span>
                    </div>
                );
            })}
        </div>
    );
}
